import React, { useMemo, useState } from "react";
import { Button } from "Components/ReactstrapSlim";
import {
   ActivityReservationInternalModel,
   ReservationMethod,
} from "Services/API/API";
import { formatStringDate, toDateOnly } from "Utils/date";
import { AvatarCircleWithCache } from "Components/AvatarCircle";
import { useActivity } from "../hooks";

import classes from "./ActivityList.module.scss";
import { useSelector } from "react-redux";
import { userSelector } from "Utils/selectors";
import { ReservationDetailsModal } from "features/EnterprisePortal/Components/Reservations/DetailsModal";

import moment from "moment";
import { MemberProfile } from "../MemberProfile/MemberProfile";
import { createPortal } from "react-dom";
import { formatReservationTime } from "Utils/datetime";

interface ActivityCardProps {
   activity: ActivityReservationInternalModel;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity }) => {
   const [reservationDetails, setReservationDetails] = useState<{ id: Guid, occurrence?: string } | null>(null);
   const [memberProfileId, setMemberProfileId] = useState<Guid | undefined>(undefined);
   const user = useSelector(userSelector);
   const { userPictureCache, openPrebin, modalContainer, reloadActivities } = useActivity();
   const organizerName = useMemo(
      () => activity.memberFullName || activity.memberEmail || "Unknown",
      [activity]
   );
   const [firstName, lastName] = useMemo(
      () => organizerName?.split(" "),
      [organizerName]
   );

   const onReservationClick = async (ev: React.SyntheticEvent) => {
      ev.stopPropagation();
      setReservationDetails({ id: activity.reservationId, occurrence: activity.occurrenceDate });
   };

   const onJoinClick = (ev: React.SyntheticEvent) => {
      ev.stopPropagation();
      const today = moment().startOf("day");
      const isPast = activity.endDate 
         && moment(activity.endDate).startOf("day")
            .isBefore(today);
      
      openPrebin(activity.venueId, toDateOnly(isPast ? toDateOnly(today.toISOString()) : activity.startDate));
   };

   const onMemberProfileClick = (ev: React.SyntheticEvent) => {
      ev.stopPropagation();
      setMemberProfileId(activity.memberId);
   }

   const worksText = useMemo(() => {
      // guestCounts - Total attending
      const { guestsCount = 0, endDate } = activity;
      
      const isPast = endDate && moment(endDate).isBefore(moment());
      const workTextPural = isPast ? "worked" : "are working";
      const inviteesCount = guestsCount - 1;
      if (inviteesCount === 1) {
         return `and 1 colleague ${workTextPural}`;
      }
      if (inviteesCount > 1) {
         return `and ${inviteesCount} colleagues ${workTextPural}`;
      }
      if (inviteesCount > 10) {
         return `and 10+ colleagues ${workTextPural}`;
      }
      return isPast ? "worked" : "is working";
   }, [activity]);

   const dateText = useMemo(() => {
      const { startDate, endDate, reservationType } = activity;
      return `${(reservationType === ReservationMethod.DayPass ? "Day Pass" : "from")} ${formatReservationTime(startDate, endDate)}`;
   }, [activity]);

   return (
      <>
         <div className={classes.card}>
            <div onClick={onMemberProfileClick}>
               <AvatarCircleWithCache
                  userId={activity.memberId}
                  cache={userPictureCache}
                  firstName={firstName}
                  lastName={lastName}
                  size="lg"
               />
            </div>
            <div className={classes.cardContent} onClick={onReservationClick}>
               <div className={classes.cardRow}>
                  <div className={classes.cardTitle}>{organizerName}</div>
                  <Button color="primary" size="sm" className={classes.cardJoinAction} onClick={onJoinClick}>                        
                     {user.id == activity.memberId ? "Book Again" : "Work Here" }
                  </Button>
               </div>
               <div className={classes.cardSubtitle}>
                  <div className={classes.cardDescription}>{worksText} at</div>
                  <div className={classes.cardDescription}>
                     <a
                        href={activity.venueXPPUrl}
                        target="_blank"
                        className={classes.cardLink}
                        onClick={(e) => e.stopPropagation()}
                     >
                        {activity.venueName}
                     </a>
                  </div>
                  <div className={classes.cardDescription}>
                     {dateText}
                  </div>
               </div>
            </div>
         </div>
         { createPortal(<>
            { reservationDetails && <ReservationDetailsModal isOpen container={modalContainer} toggle={() => setReservationDetails(null)} reservation={reservationDetails.id} occurrenceDate={reservationDetails.occurrence} onUpdateReservation={reloadActivities} /> }
            { memberProfileId && <MemberProfile isOpen={true} container={modalContainer} memberId={memberProfileId} toggle={() => setMemberProfileId(undefined)} /> }
         </>, modalContainer) }
         
      </>
   );
};

export default ActivityCard;
